import { ref, toRef, type MaybeRef } from 'vue'

import {
  GuardReport,
  ReportSummary,
  type IGuardReportActivityObservationData,
  type IGuardReportData,
  type IReportActivityData,
  type IReportObservationAttachmentData
} from '@/models/report'
import guardReportService from '@/services/guard_report/guard-report'
import guardReportActivityReportService from '@/services/guard_report/guard-report-activity'
import guardReportActivityObservationReportService from '@/services/guard_report/guard-report-activity-observation'
import guardReportActivityObservationAttachmentService from '@/services/guard_report/guard-report-activity-observation-attachment'

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import type { IReportFilterParam } from '@/services'
import { useSnackbarStore } from '@/stores'

export function useFetchGuardReports(filter?: IReportFilterParam) {
  const count = ref<number>(0)

  const queryKey = ref(['guard-reports', filter])

  const {
    data: reports,
    isFetching,
    error
  } = useQuery({
    queryKey: queryKey.value,
    queryFn: () => guardReportService.fetchGuardReports(filter),
    select: (data) => {
      count.value = data.count
      return data.results.map((report) => new GuardReport(report))
    }
  })

  return { queryKey, reports, count, isFetching, error }
}

export function useFetchGuardReport(id: number) {
  const queryKey = ref(['guard-report', id])

  const {
    data: report,
    isLoading,
    error
  } = useQuery({
    queryKey: queryKey.value,
    queryFn: () => guardReportService.fetchGuardReport(id),
    select: (data) => new GuardReport(data)
  })

  return { report, isLoading, error, queryKey }
}

// TODO - LH  - 2024-01-31 - Check why inferred typing of useMutation cannot be found
export function useDeleteGuardReport() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['delete-guard-report'],
    mutationFn: (reportId: number) => guardReportService.deleteGuardReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['guard-reports']) })
      snackbarStore.showSnackbar('Guard Report deleted successfully', '')
    }
  })
  return {
    deleteInProgress: isPending,
    isDeleteError: isError,
    deleteError: error,
    isDeleteSuccess: isSuccess,
    deleteGuardReport: mutate,
    clearDeleteErrors: reset
  }
}

export function useCreateGuardReport() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['create-guard-report'],
    mutationFn: (report: IGuardReportData) => guardReportService.createGuardReport(report),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ref(['guard-report', data.id]) })
      queryClient.invalidateQueries({ queryKey: ref(['guard-reports']) })
      snackbarStore.showSnackbar('Guard Report created successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useUpdateGuardReport() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-guard-report'],
    mutationFn: (report: IGuardReportData) => guardReportService.updateGuardReport(report),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ref(['guard-report', data.id]) })
      queryClient.invalidateQueries({ queryKey: ref(['guard-reports']) })
      snackbarStore.showSnackbar('Guard Report updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useShareGuardReports() {
  const snackbarStore = useSnackbarStore()

  const mutationKey = ref(['share-guard-report'])

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: mutationKey,
    mutationFn: (data: number[]) => guardReportService.shareGuardReports(data),

    onSuccess: () => {
      snackbarStore.showSnackbar('Guard Reports shared successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

// Activities
export function useCreateGuardReportActivity(reportId: number) {
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['create-guard-report-activity'],
    mutationFn: () => guardReportActivityReportService.createGuardReportActivity(reportId),
    onSuccess: () => {
      snackbarStore.showSnackbar('Report activity created successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useUpdateGuardReportActivity(reportId: number) {
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-guard-report-activity'],
    mutationFn: (activity: IReportActivityData) =>
      guardReportActivityReportService.updateGuardReportActivity(reportId, activity),
    onSuccess: () => {
      snackbarStore.showSnackbar('Report activity updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useDeleteGuardReportActivity(reportId: number) {
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['delete-guard-report-activity'],
    mutationFn: (activityId: number) =>
      guardReportActivityReportService.deleteGuardReportActivity(reportId, activityId),
    onSuccess: () => {
      snackbarStore.showSnackbar('Report activity deleted successfully', '')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

// Activity Observations

export function useCreateGuardReportActivityObservation(
  reportId: number,
  activityId: MaybeRef<number>
) {
  const activityIdRef = toRef(activityId)
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['create-guard-report-activity-observation', activityIdRef.value],
    mutationFn: (data: IGuardReportActivityObservationData) =>
      guardReportActivityObservationReportService.createGuardReportActivityObservation(
        reportId,
        activityIdRef.value,
        data
      ),
    onSuccess: () => {
      snackbarStore.showSnackbar('Report activity observation created successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useUpdateGuardReportActivityObservation(
  reportId: number,
  activityId: MaybeRef<number>
) {
  const activityIdRef = toRef(activityId)
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['update-guard-report-activity-observation', activityIdRef.value],
    mutationFn: (observation: IGuardReportActivityObservationData) =>
      guardReportActivityObservationReportService.updateGuardReportActivityObservation(
        reportId,
        activityIdRef.value,
        observation
      ),
    onSuccess: () => {
      snackbarStore.showSnackbar('Report activity observation updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useDeleteGuardReportActivityObservation(
  reportId: number,
  activityId: MaybeRef<number>
) {
  const activityIdRef = toRef(activityId)
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['delete-guard-report-activity-observation', activityIdRef.value],
    mutationFn: (observationId: number) =>
      guardReportActivityObservationReportService.deleteGuardReportActivityObservation(
        reportId,
        activityIdRef.value,
        observationId
      ),
    onSuccess: () => {
      snackbarStore.showSnackbar('Report activity observation deleted successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

// Activity Observation Attachments
export function useCreateGuardReportActivityObservationAttachment(
  reportId: MaybeRef<number>,
  activityId: MaybeRef<number>,
  observationId: MaybeRef<number>
) {
  const reportIdRef = toRef(reportId)
  const activityIdRef = toRef(activityId)
  const observationIdRef = toRef(observationId)
  const queryClient = useQueryClient()

  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: [
      'create-guard-report-activity-observation',
      activityIdRef.value,
      observationIdRef.value
    ],
    mutationFn: (data: IReportObservationAttachmentData) => {
      return guardReportActivityObservationAttachmentService.createGuardReportActivityObservationAttachment(
        reportIdRef.value,
        activityIdRef.value,
        observationIdRef.value,
        data
      )
    },

    onSuccess: (attachment) => {
      queryClient.setQueryData(
        ['guard-report', reportIdRef.value],
        (oldGuardReport?: IGuardReportData) => {
          if (oldGuardReport) {
            const activities = oldGuardReport.activities!

            let observations: IGuardReportActivityObservationData[] = []

            const activityIndex = activities.findIndex((v) => v.id === activityIdRef.value)

            if (activityIndex !== -1) {
              const observationIndex = activities[activityIndex]!.observations!.findIndex(
                (v) => v.id === observationIdRef.value
              )
              if (observationIndex !== -1) {
                observations = activities[activityIndex]!.observations!

                observations[observationIndex].attachments?.push(attachment)

                activities[activityIndex].observations = observations
              }
            }

            // https://tanstack.com/query/v4/docs/framework/vue/guides/updates-from-mutation-responses#immutability
            return {
              ...oldGuardReport,
              activities: activities
            }
          }

          return oldGuardReport
        }
      )
      snackbarStore.showSnackbar('Report activity observation attachment created successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useUpdateGuardReportActivityObservationAttachment(
  reportId: number,
  activityId: MaybeRef<number>,
  observationId: MaybeRef<number>
) {
  const activityIdRef = toRef(activityId)
  const observationIdRef = toRef(observationId)
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: [
      'create-guard-report-activity-observation',
      activityIdRef.value,
      observationIdRef.value
    ],
    mutationFn: (data: IReportObservationAttachmentData) =>
      guardReportActivityObservationAttachmentService.updateGuardReportActivityObservationAttachment(
        reportId,
        activityIdRef.value,
        observationIdRef.value,
        data
      ),

    onSuccess: () => {
      snackbarStore.showSnackbar('Report activity observation attachment updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useDeleteGuardReportActivityObservationAttachment(
  reportId: number,
  activityId: MaybeRef<number>,
  observationId: MaybeRef<number>
) {
  const activityIdRef = toRef(activityId)
  const observationIdRef = toRef(observationId)
  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: [
      'create-guard-report-activity-observation',
      activityIdRef.value,
      observationIdRef.value
    ],
    mutationFn: (attachmentId: number) =>
      guardReportActivityObservationAttachmentService.deleteGuardReportActivityObservationAttachment(
        reportId,
        activityIdRef.value,
        observationIdRef.value,
        attachmentId
      ),

    onSuccess: () => {
      snackbarStore.showSnackbar('Report activity observation attachment deleted successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

// Summary
export function useFetchGuardReportSummaries(reportId: number) {
  const queryKey = ref(['guard-report-summaries', reportId])

  const {
    data: summaries,
    isFetching,
    isPending,
    error
  } = useQuery({
    queryKey: queryKey.value,
    queryFn: () => guardReportService.fetchGuardReportSummaries(reportId),

    select: (data) => {
      return data.map((summary) => new ReportSummary(summary))
    }
  })

  return { queryKey, summaries, isFetching, isPending, error }
}

export function useFetchGuardReportSummary(reportId: number, id: MaybeRef<number>) {
  const summaryRef = toRef(id)

  const queryKey = ref(['guard-report-summary', summaryRef.value])

  const {
    data: summary,
    isLoading,
    isPending,
    error
  } = useQuery({
    queryKey: queryKey.value,
    queryFn: () => guardReportService.fetchGuardReportSummary(reportId, summaryRef.value),

    select: (data) => new ReportSummary(data)
  })

  return { summary, isLoading, isPending, error, queryKey }
}

export function useCreateGuardReportSummary(reportId: number) {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['create-guard-report-summary'],
    mutationFn: (summary: IReportActivityData) =>
      guardReportService.createGuardReportSummary(reportId, summary),

    onSuccess: (data) => {
      queryClient.setQueryData(['guard-report-summary', data.id], data)
      snackbarStore.showSnackbar('Guard Report Summary created successfully')
    },
    onSettled: async () =>
      await queryClient.invalidateQueries({ queryKey: ['guard-report-summaries'] })
  })

  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useUpdateGuardReportSummary(reportId: number) {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-guard-report-summary'],
    mutationFn: (summary: IReportActivityData) =>
      guardReportService.updateGuardReportSummary(reportId, summary),

    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ref(['guard-report-summary', data.id]) })
      queryClient.invalidateQueries({ queryKey: ref(['guard-report-summaries']) })
      snackbarStore.showSnackbar('Guard Report Summary updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useDeleteGuardReportSummary(reportId: number) {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['delete-guard-report-summary'],
    mutationFn: (summaryId: number) =>
      guardReportService.deleteGuardReportSummary(reportId, summaryId),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['guard-report-summaries']) })
      snackbarStore.showSnackbar('Guard Report Summary deleted successfully', '')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}
